import * as _ from 'underscore';

import { IPublishedConnectionColumnInfo, IPublishedConnectionTableDetailInfo, IPublishedConnectionTableInfo } from '../models/IPublishedConnectionTableInfo';
import { IUserInfo } from '../models/IUserInfo';
import { IDownstreamDatasourcesConnectionInfo, IDownstreamWorkbooksConnectionInfo, INamedItemInfo } from '../models/resourceInfoParts';
import { IPublishedConnectionColumn, IPublishedConnectionTable, IPublishedConnectionTableDetail } from '../ServerApi/types/IPublishedConnectionTable';
import { IPublishedConnectionTableUsageInfo } from '../ServerApi/types/IUsageInfo';
import { MapperHelper } from './MapperHelper';
import { ProjectMapper } from './ProjectMapper';
import { UserMapper } from './UserMapper';

export module PublishedConnectionTableMapper {
  const M = MapperHelper;

  export function map(
    serverApiMetrics: IPublishedConnectionTable[],
    users: _.Dictionary<IUserInfo>,
    projects: _.Dictionary<INamedItemInfo>,
    favoritePublishedConnectionTables: _.Dictionary<any> = {}
  ): IPublishedConnectionTableInfo[] {
    return serverApiMetrics.map((m: IPublishedConnectionTable) => {
      return mapOne(m, users[m.ownerId], projects[m.projectId], !!favoritePublishedConnectionTables[m.id]);
    });
  }

  export function mapOne(
    publishedConnectionTable: IPublishedConnectionTable,
    owner?: IUserInfo,
    project?: INamedItemInfo,
    favorite?: boolean
  ): IPublishedConnectionTableInfo {
    return {
      createdAt: M.toDate(publishedConnectionTable.createdAt),
      updatedAt: M.toDate(publishedConnectionTable.updatedAt),
      tags: M.sortTags(publishedConnectionTable.tags),
      description: publishedConnectionTable.description,
      id: publishedConnectionTable.id,
      luid: publishedConnectionTable.luid,
      name: publishedConnectionTable.name,
      owner: UserMapper.mapOne(owner),
      favorite: favorite,
      contentType: 'publishedConnectionTable',
      project: project,
      alias: publishedConnectionTable.alias,
      databaseName: publishedConnectionTable.databaseName,
      schemaName: publishedConnectionTable.schemaName,
      hasDataPolicy: publishedConnectionTable.hasDataPolicy,
      publishedConnectionId: publishedConnectionTable.publishedConnectionId,
      dataQualityIndicators:  publishedConnectionTable.dataQualityIndicators || [],
      hasExtracts: publishedConnectionTable.hasExtract,
      lastRefreshedAt: publishedConnectionTable.hasExtract ? M.toDate(publishedConnectionTable.extractsRefreshedAt) : undefined,
      usageInfo: <IPublishedConnectionTableUsageInfo>{favoritesTotal: 0},
      usageInfoMaxValues: <IPublishedConnectionTableUsageInfo>{},
    };
  }

  export function mapDetail(p: IPublishedConnectionTableDetail): IPublishedConnectionTableDetailInfo {
    return {
      createdAt: M.toDate(p.createdAt),
      updatedAt: M.toDate(p.updatedAt),
      tags: M.sortTags(p.tags),
      description: p.description,
      id: p.id,
      luid: p.luid,
      name: p.name,
      owner: UserMapper.mapOne(p.owner),
      favorite: p.favorite,
      contentType: 'publishedConnectionTable',
      project: ProjectMapper.mapName(p.project),
      alias: p.alias,
      databaseName: p.databaseName,
      schemaName: p.schemaName,
      hasDataPolicy: p.hasDataPolicy,
      publishedConnectionId: p.publishedConnectionId,
      dataQualityIndicators:  p.dataQualityIndicators || [],
      hasExtracts: p.hasExtract,
      lastRefreshedAt: p.hasExtract ? M.toDate(p.extractsRefreshedAt) : undefined,
      columns: mapColumns(p.columns)
    };
  }

  export function mapColumns(columns: IPublishedConnectionColumn[]): IPublishedConnectionColumnInfo[] {
    return columns.map(c => {
      return {
        id: c.luid,
        name: c.name,
        luid: c.luid,
        downstreamDatasourcesConnection: mapDownstreamDatasources(c),
        downstreamWorkbooksConnection: mapDownstreamWorkbooks(c),
        dataType: c.dataType,
      } as IPublishedConnectionColumnInfo;
    });
  }

  export function mapFromCRASS(publishedConnectionTable: any): IPublishedConnectionTableInfo {
    return {
      createdAt: M.toDate(publishedConnectionTable.createdAt),
      updatedAt: M.toDate(publishedConnectionTable.modifiedTime),
      tags: M.sortTags(publishedConnectionTable.tags),
      description: publishedConnectionTable.description,
      id: publishedConnectionTable.id,
      luid: publishedConnectionTable.luid,
      name: publishedConnectionTable.title,
      owner: UserMapper.mapOneOwnerFromCRASS(publishedConnectionTable),
      favorite: false,
      contentType: 'publishedConnectionTable',
      project: {
        id: publishedConnectionTable.projectId ?? publishedConnectionTable.parentProjectId,
        name: publishedConnectionTable.containerName
      },
      alias: publishedConnectionTable.alias,
      databaseName: publishedConnectionTable.databaseName,
      schemaName: publishedConnectionTable.schemaName,
      hasDataPolicy: publishedConnectionTable.hasDataPolicy,
      publishedConnectionId: publishedConnectionTable.publishedConnectionId || publishedConnectionTable.parentPublishedConnectionId,
      dataQualityIndicators:  publishedConnectionTable.dataQualityIndicators || [],
      hasExtracts: publishedConnectionTable.hasExtract,
      lastRefreshedAt: publishedConnectionTable.hasExtract ? M.toDate(publishedConnectionTable.extractsRefreshedAt) : undefined,
      usageInfo: <IPublishedConnectionTableUsageInfo>{favoritesTotal: 0},
      usageInfoMaxValues: <IPublishedConnectionTableUsageInfo>{},
    };
  }

  function mapDownstreamWorkbooks(column: IPublishedConnectionColumn): IDownstreamWorkbooksConnectionInfo {
    return {
      totalCount: column.downstreamWorkbookCount
    };
  }

  function mapDownstreamDatasources(column: IPublishedConnectionColumn): IDownstreamDatasourcesConnectionInfo {
    return {
      totalCount: column.downstreamDataSourceCount
    };
  }
}
